import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function PrivacySecurity() {
  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/privacy-security`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Privacy & Security", "item": pageUrl }
    ]
  };

  return (
    <>
      <Head>
        <title>Privacy & Security | Modern Build</title>
        <meta name="title" content="Privacy & Security | Modern Build" />
        <meta
          name="description"
          content="Learn how Modern Build collects, secures, and uses your personal information. We’re committed to transparency and privacy protection."
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta name="keywords" content="privacy, security, data protection, reCAPTCHA, analytics, cookies, Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/privacy-security" />

        {/* Open Graph */}
        <meta property="og:title" content="Privacy & Security | Modern Build" />
        <meta property="og:description" content="Review our privacy and security practices, including how we protect your personal information and keep your data safe." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/privacy-security" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy & Security | Modern Build" />
        <meta name="twitter:description" content="Discover how Modern Build protects your data and privacy. We use SSL, reCAPTCHA, and secure data practices to safeguard your experience." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Privacy & Security | Modern Build',
                description: 'Learn how Modern Build protects your privacy and secures your personal data on our website.',
                slug: '/privacy-security'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      </Head>

      <Header />
      <Hero category="PrivacySecurity" page="page" hasContentH1={true}/>
      <div className="container mx-auto px-4 py-10 max-w-4xl">
  <div className="prose prose-lg max-w-none text-md text-black">
    <h1 className="text-3xl font-gilda mb-6">Privacy &amp; Security</h1>

    <p>
      At Modern Build Home Remodeling, we are committed to safeguarding your privacy and data security. This page explains how we collect, use, share, and protect your information when you use our website.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">1. What We Collect</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>Contact information when you fill out forms or request estimates</li>
      <li>Project details you voluntarily provide</li>
      <li>Device, IP address, browser info, and approximate location collected automatically</li>
      <li>Cookie and tracking data that help improve functionality and experience</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">2. Why We Collect It</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>To respond to inquiries and provide estimates you request</li>
      <li>To connect you with independent licensed contractors when you ask for referrals</li>
      <li>To improve the design and usability of our website</li>
      <li>To send relevant updates or promotions if you opt in</li>
      <li>To ensure site security using tools like Google reCAPTCHA v3</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">3. Our Role and Contractor Referrals</h2>
    <p className="mb-4">
      Modern Build Home Remodeling does not perform construction or remodeling work. Physical work is performed by independent licensed, bonded, and insured contractors who operate their own businesses. If you request an estimate or referral, we may share your contact details and project information with one or more contractors so they can contact you directly. We do not supervise or control contractor work, and we are not responsible for contractor performance, workmanship, permits, or compliance. Any service agreements, warranties, and payments are between you and the contractor you choose.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">4. Analytics, Cookies, and Similar Technologies</h2>
    <p className="mb-4">
      We use cookies to remember preferences and to analyze site usage. We also use Google Analytics for traffic insights and Google reCAPTCHA to help prevent spam and abuse. You can control cookies in your browser settings, some features may not function properly if cookies are disabled.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">5. How We Share Information</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li><span className="font-semibold">Contractor referrals.</span> Shared with independent contractors only when needed to fulfill your request</li>
      <li><span className="font-semibold">Service providers.</span> Hosting, analytics, security, and communication tools that process data for us under agreements that limit their use</li>
      <li><span className="font-semibold">Legal compliance.</span> When required by law or to protect rights and safety</li>
    </ul>
    <p className="mb-4">
      We do not sell your personal information. We do not share your information for cross contextual advertising.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">6. Data Protection</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>SSL encryption secures data in transit</li>
      <li>Access is limited to authorized personnel</li>
      <li>Regular updates, monitoring, and firewalls help protect our systems</li>
    </ul>
    <p className="mb-4">
      No security method is perfect, we cannot guarantee absolute protection.
    </p>
    <p className="mb-4">
      All images and videos displayed on this website are generated using artificial intelligence and are not photographs or recordings of actual projects. They are provided for illustrative purposes only and should not be interpreted as real work performed by Modern Build or its contractors.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">7. Your Rights and Choices</h2>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>Request access to, correction of, or deletion of your personal data</li>
      <li>Request that we do not share your details with contractors, this may limit our ability to provide estimates</li>
      <li>Opt out of marketing communications at any time</li>
      <li>Control cookie preferences in your browser</li>
    </ul>
    <p className="mb-4">
      California residents may exercise rights under the CCPA and CPRA. To submit a request, email <a href="mailto:remodel@modernbuildremodel.com" className="underline">remodel@modernbuildremodel.com</a>.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">8. Data Retention</h2>
    <p className="mb-4">
      We retain personal information only as long as needed for the purposes described, to meet legal obligations, or as otherwise disclosed to you. When no longer needed, we delete or anonymize it.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">9. Children’s Privacy</h2>
    <p className="mb-4">
      Our website is intended for adults. We do not knowingly collect personal information from children under 13.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">10. Contact</h2>
    <p>
      For questions about our privacy and security practices, or to exercise your rights, contact us:
    </p>
    <ul className="list-disc pl-6 space-y-2 mb-4">
      <li>Phone: (323) 300 4130</li>
      <li>Email: <a href="mailto:remodel@modernbuildremodel.com" className="underline">remodel@modernbuildremodel.com</a></li>
      <li>Address: 4222 Longridge Ave, Studio City, CA 91604, USA</li>
    </ul>

    <p className="text-sm text-gray-600 mt-6">Last updated, September 2025</p>
  </div>
      </div>
      <Footer />
    </>
  );
}